import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * SectionNav — floating dot navigation on the right edge of the viewport.
 * Highlights the currently visible section and allows click-to-scroll.
 * Desktop only (hidden on < 768px).
 *
 * Uses a scroll listener + getBoundingClientRect for reliable tracking
 * regardless of section height differences.
 */

const SECTIONS = [
  { id: 'home', label: 'Home' },
  { id: 'why-alphanetix', label: 'Platform' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'partners', label: 'Partners' },
  { id: 'industries', label: 'Industries' },
  { id: 'technologies', label: 'Technology' },
  { id: 'contact', label: 'Get Started' },
];

export default function SectionNav() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const isMobile = useMediaQuery('(max-width:768px)');
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef(null);

  const handleClick = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const update = () => {
      const vh = window.innerHeight;
      // Anchor line: 35% from top of viewport — biases toward "entering" sections
      const anchor = vh * 0.35;
      let bestIdx = 0;
      let bestDist = Infinity;

      for (let i = 0; i < SECTIONS.length; i++) {
        const el = document.getElementById(SECTIONS[i].id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // Section spans from rect.top to rect.bottom
        // If anchor is inside the section, distance = 0
        // Otherwise, distance = gap to nearest edge
        const dist = anchor < rect.top
          ? rect.top - anchor
          : anchor > rect.bottom
            ? anchor - rect.bottom
            : 0; // anchor is inside this section

        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      }

      setActiveIndex(bestIdx);
      rafRef.current = null;
    };

    const onScroll = () => {
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(update);
      }
    };

    // Initial check
    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isMobile]);

  if (isMobile) return null;

  return (
    <Box
      component="nav"
      aria-label="Section navigation"
      sx={{
        position: 'fixed',
        right: 20,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        alignItems: 'center',
      }}
    >
      {SECTIONS.map((section, i) => {
        const isActive = i === activeIndex;
        return (
          <Tooltip key={section.id} title={section.label} placement="left" arrow>
            <Box
              component="button"
              onClick={() => handleClick(section.id)}
              aria-label={`Go to ${section.label}`}
              aria-current={isActive ? 'true' : undefined}
              sx={{
                width: isActive ? 10 : 6,
                height: isActive ? 10 : 6,
                borderRadius: '50%',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: isActive
                  ? (isLight ? '#6366f1' : '#10b981')
                  : isLight
                    ? 'rgba(0,0,0,0.2)'
                    : 'rgba(255,255,255,0.25)',
                boxShadow: isActive
                  ? '0 0 8px rgba(99, 102, 241,0.5)'
                  : 'none',
                '&:hover': {
                  backgroundColor: isLight ? '#6366f1' : '#10b981',
                  transform: 'scale(1.4)',
                },
                '&:focus-visible': {
                  outline: `2px solid ${isLight ? '#6366f1' : '#10b981'}`,
                  outlineOffset: 3,
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}
